import { Injectable } from '@angular/core';
import { combineLatest } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AdminService } from '../../../admin/admin.service';
import { IUserInfo } from '../../interfaces/user';
import { IAnalyticsService } from './ianalyticsservice';

interface IPendoWindow extends Window {
  pendo?: any;
}
declare const window: IPendoWindow;

@Injectable({
  providedIn: 'root'
})
export class PendoService {
  corporationName;
  userInfo: IUserInfo;
  pendoReady = false;

  /* This can be used/adapted for any events that might need to be sent */
  private pendoEvent: IAnalyticsService | undefined;

  constructor(private adminService: AdminService) {}

  /**
   * Init Pendo
   * @param productAPIKey relative to the app in Pendo
   * @param pendoObject Option to send custom user stats to Pendo.
   * Using {} will let Pendo generate anonymous id's uf you have no identifying data
   */
  initPendoScript(productAPIKey: string, pendoObject?: any): void {
    if (environment.enableAnalytics) {
      this.injectPendo(productAPIKey);

      if (pendoObject && !this.pendoReady)
        this.initPendo(pendoObject);
      else {
        combineLatest([
          this.adminService.corporationName,
          this.adminService.userInfo
        ]).subscribe(([name, userInfo]: [string, IUserInfo]) => {
          this.corporationName = name;
          this.userInfo = userInfo;
          if (name && userInfo) {
            this.initPendo();
          }
        });
      }
    }
  }

  private injectPendo(productAPIKey: string): void {
    (function (p: any, e: Document, n: string, d: string, o?: any): void {
      const v: string[] = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
      let w: number, x: number, y: HTMLScriptElement, z: Node | null;
      o = p[d] = p[d] || {};
      o._q = o._q || [];
      for (w = 0, x = v.length; w < x; ++w) {
        ((m: string) => {
          o[m] =
            o[m] ||
            function (...args: any[]): void {
              o._q[m === v[0] ? 'unshift' : 'push']([m].concat(args));
            };
        })(v[w]);
      }
      y = e.createElement(n) as HTMLScriptElement;
      y.async = true;
      y.src = `https://cdn.eu.pendo.io/agent/static/${productAPIKey}/pendo.js`;
      z = e.getElementsByTagName(n)[0];
      if (z && z.parentNode) {
        z.parentNode.insertBefore(y, z);
      }
    })(window as any, document, 'script', 'pendo');
  }

  private initPendo(pendoObject?: any): void {
    if (window.pendo) {
      if (pendoObject) {
        window.pendo.initialize({
          pendoObject
        });
        this.pendoReady = true;
      } else {
        window.pendo.initialize({
          visitor: { id: `${this.userInfo.userId}-${this.userInfo.corpId}`},
          account: {
            id: `bullhorn-${this.userInfo.corpId}`,
            name: this.corporationName
          }
        });
      }
    }
  }

  /**
   * Records a custom Pendo Track Event. No-ops outside production analytics builds or before pendo.js loads.
   */
  recordEvent(eventType: string, eventData?: Record<string, unknown>): void {
    if (!environment.enableAnalytics || !window.pendo || typeof window.pendo.track !== 'function') {
      return;
    }
    try {
      window.pendo.track(eventType, eventData ?? {});
    } catch (err) {
      console.warn('Pendo track failed:', err);
    }
  }

  /**
   * Pendo caps event properties at 512 bytes. Truncate strings before sending,
   * and never send raw user-entered text that could contain PII.
   */
  truncateForPendo(value: unknown, maxLength = 50): string {
    if (value === null || value === undefined) {
      return '';
    }
    const str = String(value);
    return str.length > maxLength ? `${str.slice(0, maxLength)}…` : str;
  }
}
